import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  LocalHospital as HospitalIcon,
  EventNote as EventIcon,
  AccessTime as TimeIcon,
  ShowChart as ChartIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const LandingSidebar = () => {
  const { openAuthModal } = useAuth();

  const programStats = [
    { icon: <HospitalIcon color="primary" />, number: '150+', label: 'Clinical Trials', desc: 'Active breast cancer trials across Florida' },
    { icon: <PeopleIcon color="success" />, number: '3,500+', label: 'Patients Enrolled', desc: 'Breast cancer patients in trials this year' },
    { icon: <BusinessIcon color="secondary" />, number: '45+', label: 'Research Centers', desc: 'Leading cancer research institutions' },
    { icon: <EventIcon color="info" />, number: '12', label: 'New Trials', desc: 'Recently opened mammography trials' },
    { icon: <TimeIcon color="warning" />, number: '7 days', label: 'Average Matching', desc: 'AI-powered trial matching time' },
    { icon: <ChartIcon color="success" />, number: '92%', label: 'Match Success', desc: 'Successful trial-patient matching rate' }
  ];

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sign In/Sign Up Card - replaces user profile */}
      <Card sx={{ 
        mb: 3,
        background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
        color: 'white'
      }}>
        <CardContent sx={{ textAlign: 'center' }}>
          <Box sx={{ 
            width: 60, 
            height: 60, 
            borderRadius: '50%', 
            bgcolor: 'rgba(255,255,255,0.2)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            border: '2px solid rgba(255,255,255,0.3)',
            mx: 'auto',
            mb: 2
          }}>
            <Typography variant="h4">🩺</Typography>
          </Box>
          
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Welcome to MammoTrials
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
            Access personalized clinical trial matching and AI-powered recommendations
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button 
              onClick={() => openAuthModal('signin')}
              variant="contained"
              startIcon={<LoginIcon />}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.9)', 
                color: 'primary.main',
                '&:hover': { bgcolor: 'white' }
              }}
            >
              Sign In
            </Button>
            <Button 
              onClick={() => openAuthModal('signup')}
              variant="outlined"
              startIcon={<PersonAddIcon />}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { 
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)' 
                }
              }}
            >
              Sign Up
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Clinical Trials Impact Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon color="primary" />
          Clinical Trials Impact
        </Typography>
      </Box>
      
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Program Impact Stats */}
        <List sx={{ py: 0 }}>
          {programStats.map((stat, index) => (
            <ListItem key={index} sx={{ py: 1, px: 0 }}>
              <Card sx={{ width: '100%', boxShadow: 1, '&:hover': { boxShadow: 2 } }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ mt: 0.5 }}>
                      {stat.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                          {stat.number}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {stat.label}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {stat.desc}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </ListItem>
          ))}
        </List>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ textAlign: 'center', px: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            MammoTrials: Advancing Breast Cancer Care
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Connecting patients with life-saving clinical trials through AI-powered matching and mCODE integration.
          </Typography>
        </Box>
        
        {/* MammoAssist Info Card */}
        <Card sx={{ 
          mt: 3, 
          background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
          color: 'white'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%', 
                bgcolor: 'rgba(255,255,255,0.2)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                border: '2px solid rgba(255,255,255,0.3)'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>M</Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>MammoAssist™</Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                AI-powered clinical trial matching available after sign up:
              </Typography>
              
              <List dense sx={{ py: 0, mb: 2 }}>
                {[
                  { icon: '🎯', text: 'Clinical trial matching with mCODE data' },
                  { icon: '🤖', text: 'AI-powered eligibility assessment' },
                  { icon: '💡', text: 'Personalized breast cancer trial recommendations' }
                ].map((item, idx) => (
                  <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                      <Typography>{item.icon}</Typography>
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{ variant: 'caption', color: 'inherit' }}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Button 
                onClick={() => openAuthModal('signup')}
                variant="contained" 
                fullWidth 
                startIcon={<PersonAddIcon />}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.9)', 
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'white' }
                }}
              >
                Sign Up to Access MammoAssist™
              </Button>
            </Box>
            
            <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.9 }}>
              Join thousands of patients finding hope!
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LandingSidebar;